import React, { useState } from "react";
import { Form, Button, Table, Badge, Spinner, Alert } from "react-bootstrap";
import { FaSearch, FaRedo } from "react-icons/fa";
import { Users, Key, Calendar } from "lucide-react";
import AdminLayout from "../../components/AdminLayout";
import OtpLookup from "./OtpLookup";
import CustomerStatusModal from "./CustomerStatusModal";
import api from "../../api";
import "../../styles/OtpLookupPage.css";

// ── Helpers ───────────────────────────────────────────────
const fmtOut = (val) => {
  const n = parseFloat(val);
  if (isNaN(n)) return "—";
  return n.toLocaleString("en-LK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const outColor = (val) => {
  const n = parseFloat(val);
  if (isNaN(n) || n === 0) return "#64748b";
  return n > 0 ? "#dc2626" : "#16a34a";
};
const today = new Date().toLocaleDateString("en-US", {
  weekday: "long", year: "numeric", month: "long", day: "numeric",
});

// ── Customer Profile Panel ────────────────────────────────
const CustomerPanel = () => {
  const [code, setCode]           = useState("");
  const [inputCode, setInputCode] = useState("");
  const [branches, setBranches]   = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [statusModal, setStatusModal] = useState(null); // { customerCode, branchCode, outstanding }

  const search = async (e) => {
    e.preventDefault();
    if (!inputCode.trim()) return;
    setError(""); setBranches(null); setLoading(true);
    try {
      const { data } = await api.get("/api/otp-lookup/branches", {
        params: { customerCode: inputCode.trim() },
      });
      setBranches(data);
      setCode(inputCode.trim());
    } catch (err) {
      setError(err.response?.data?.error || "No branches found for this customer code.");
    } finally {
      setLoading(false);
    }
  };

  const clear = () => { setInputCode(""); setCode(""); setBranches(null); setError(""); };

  const totalOut       = branches?.reduce((s, b) => s + (parseFloat(b.Outstanding) || 0), 0) ?? 0;
  const totalBranches  = branches?.length ?? 0;
  const registeredCnt  = branches?.filter((b) => b.IsRegistered).length ?? 0;
  const customerName   = branches?.[0]?.BranchName ?? "";
  const allRegistered  = totalBranches > 0 && registeredCnt === totalBranches;
  const noneRegistered = registeredCnt === 0;
  const partialReg     = !allRegistered && !noneRegistered;

  return (
    <>
      {/* Status modal */}
      {statusModal && (
        <CustomerStatusModal
          customerCode={statusModal.customerCode}
          branchCode={statusModal.branchCode}
          outstanding={statusModal.outstanding}
          onClose={() => setStatusModal(null)}
        />
      )}

      {/* Panel header */}
      <div className="csd-customer-header">
        <div className="csd-customer-header-icon">
          <Users size={17} color="#16a34a" />
        </div>
        <div>
          <p style={{ fontSize: "0.9rem", fontWeight: 700, color: "#0f172a", margin: 0 }}>
            Customer Profile
          </p>
          <p style={{ fontSize: "0.73rem", color: "#94a3b8", margin: 0 }}>
            View branches, balance &amp; app registration by customer code
          </p>
        </div>
      </div>

      {/* Search bar */}
      <div className="csd-search-strip">
        <Form onSubmit={search} className="d-flex gap-2 w-100">
          <Form.Control
            size="sm"
            placeholder="Enter customer code — e.g. A1031"
            value={inputCode}
            onChange={(e) => setInputCode(e.target.value.toUpperCase())}
            required
            style={{ minWidth: 0 }}
          />
          <Button size="sm" type="submit" variant="primary" disabled={loading} className="btn-primary">
            {loading ? <Spinner size="sm" /> : <><FaSearch size={11} className="me-1" />Search</>}
          </Button>
          {branches && (
            <Button size="sm" variant="outline-secondary" onClick={clear} title="Clear">
              <FaRedo size={11} />
            </Button>
          )}
        </Form>
        {error && (
          <Alert
            variant="danger"
            className="mt-2 mb-0 py-1 px-2 w-100"
            style={{ fontSize: "0.8rem", borderRadius: 8 }}
            onClose={() => setError("")}
            dismissible
          >
            {error}
          </Alert>
        )}
      </div>

      {/* Identity card — after search */}
      {branches && (
        <div className="csd-identity-card">
          <div style={{ minWidth: 0 }}>
            <p className="csd-identity-name">{customerName || "—"}</p>
            <p className="csd-identity-code">Code: {code}</p>
          </div>
          {allRegistered && (
            <Badge bg="success" className="fw-normal flex-shrink-0"
              style={{ fontSize: "0.7rem", borderRadius: 6, padding: "5px 10px", alignSelf: "flex-start" }}>
              ✓ All in App
            </Badge>
          )}
          {partialReg && (
            <Badge bg="warning" text="dark" className="fw-normal flex-shrink-0"
              style={{ fontSize: "0.7rem", borderRadius: 6, padding: "5px 10px", alignSelf: "flex-start" }}>
              ⚡ {registeredCnt} / {totalBranches} in App
            </Badge>
          )}
          {noneRegistered && (
            <Badge bg="secondary" className="fw-normal flex-shrink-0"
              style={{ fontSize: "0.7rem", borderRadius: 6, padding: "5px 10px", alignSelf: "flex-start" }}>
              Not in App
            </Badge>
          )}
        </div>
      )}


      {/* Table area */}
      <div className="csd-table-body">
        {!branches && (
          <div className="csd-empty">
            <div className="csd-empty-icon">
              <Users size={30} color="#2563eb" />
            </div>
            <p className="csd-empty-title">No customer selected</p>
            <p className="csd-empty-hint">
              Enter a customer code above to view all branches, outstanding balance, and mobile app registration status.
            </p>
          </div>
        )}

        {branches && (
          <Table className="csd-table">
            <thead>
              <tr>
                <th style={{ paddingLeft: 20 }}>Branch</th>
                <th>Name</th>
                <th>City</th>
                <th>Status</th>
                <th className="text-end">Outstanding</th>
                <th className="text-center">App</th>
                <th className="text-center" style={{ paddingRight: 20 }}></th>
              </tr>
            </thead>
            <tbody>
              {branches.map((b) => {
                const statusActive  = b.CustomerStatus === "Active";
                const statusOnDel   = b.CustomerStatus === "Call on delivery";
                const statusColor   = statusActive ? "#16a34a" : statusOnDel ? "#d97706" : "#94a3b8";
                const statusBg      = statusActive ? "#f0fdf4" : statusOnDel ? "#fffbeb" : "#f8fafc";
                const statusDotClr  = statusActive ? "#22c55e" : statusOnDel ? "#f59e0b" : "#94a3b8";

                return (
                  <tr key={b.BranchCode}>
                    <td style={{ paddingLeft: 20 }}>
                      <span className="csd-branch-code">{b.BranchCode}</span>
                    </td>
                    <td style={{ maxWidth: 220 }}>{b.BranchName || "—"}</td>
                    <td>{b.DeliveryCityName || "—"}</td>
                    <td>
                      <span
                        className="csd-status-pill"
                        style={{ background: statusBg, color: statusColor }}
                      >
                        <span className="csd-status-dot" style={{ background: statusDotClr }} />
                        {b.CustomerStatus || "—"}
                      </span>
                    </td>
                    <td
                      className="text-end font-monospace"
                      style={{ color: outColor(b.Outstanding) }}
                    >
                      {fmtOut(b.Outstanding)}
                    </td>
                    <td className="text-center">
                      {b.IsRegistered ? (
                        <Badge
                          bg="success"
                          className="fw-normal"
                          style={{ fontSize: "0.68rem", borderRadius: 6, padding: "3px 9px" }}
                        >
                          ✓ App
                        </Badge>
                      ) : (
                        <span style={{ fontSize: "0.8rem", color: "#cbd5e1" }}>—</span>
                      )}
                    </td>
                    <td className="text-center" style={{ paddingRight: 16 }}>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() =>
                          setStatusModal({
                            customerCode: b.CustomerCode,
                            branchCode: b.BranchCode,
                            outstanding: b.Outstanding,
                          })
                        }
                        style={{ borderRadius: 7, fontSize: "0.72rem", padding: "2px 10px" }}
                      >
                        Status
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </div>
    </>
  );
};

// ── Page ─────────────────────────────────────────────────
const OtpLookupPage = () => (
  <AdminLayout>
    <div className="csd-page">

      {/* Page header */}
      <div className="csd-header">
        <div className="csd-header-left">
          <div className="csd-header-icon">
            <Key size={22} color="#2563eb" />
          </div>
          <div>
            <h5 className="csd-header-title">Customer Lookup</h5>
            <p className="csd-header-sub">
              <Calendar size={12} />
              {today}
            </p>
          </div>
        </div>
        
      </div>

      {/* Two-panel dashboard */}
      <div className="csd-grid">

        {/* Left — OTP Lookup */}
        <div className="csd-panel csd-panel--otp">
          <OtpLookup />
        </div>

        {/* Right — Customer Profile */}
        <div className="csd-panel csd-panel--customer">
          <CustomerPanel />
        </div>

      </div>
    </div>
  </AdminLayout>
);

export default OtpLookupPage;
